import { useEffect, useState } from 'react';
import {
  describeError,
  getMyOperatorAccess,
  problemErrorCode,
  problemStatus,
  revokeMyOperatorGrant,
  type OperatorSelfAccess,
  type OperatorSelfGrant,
} from '@/lib/api';
import { clientStatusLabel, describeScopes, grantStatusLabel } from '@/lib/agents';
import { OPERATOR_INSTRUCTION } from '@/lib/operator-access';
import { Badge } from './ui';
import { ConnectToClaudeCard } from './ConnectToClaudeCard';

/**
 * "Minhas autorizações" em `/profile` (change platform-mcp-operator-access, tarefa 4.3): o titular
 * vê o próprio acesso pessoal ao MCP do backoffice no Claude Code e revoga a própria concessão.
 *
 * Contrato: `GET /platform/me/operator-access` e `POST /platform/me/operator-grants/{id}/revoke`,
 * de qualquer papel e só sobre a conta da sessão. Nenhuma resposta traz token, hash ou code, e nada
 * daqui vai para storage. O cartão some quando não há o que mostrar (caminho desligado e nenhum
 * cliente nem concessão, ou API anterior à rota).
 */

export const MY_OPERATOR_ACCESS_TITLE = 'Minhas autorizações';
export const REVOKE_CONFIRM_TEXT =
  'Revogar esta autorização? A próxima chamada do seu Claude Code ou claude.ai ao MCP do backoffice recebe 401. Para voltar, você conecta e aprova de novo, com o código do autenticador.';

const NOT_FOUND_MESSAGE = 'Esta autorização não existe mais ou não é sua. A lista foi recarregada.';

type LoadState =
  | { status: 'loading' }
  | { status: 'hidden' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; access: OperatorSelfAccess };

function formatDateTime(iso: string | null | undefined, empty = '—'): string {
  if (!iso) return empty;
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MyOperatorAccessCard() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadTick, setReloadTick] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyOperatorAccess()
      .then((access) => {
        if (cancelled) return;
        const nothingToShow = !access.enabled && access.client === null && access.grants.length === 0;
        setState(nothingToShow ? { status: 'hidden' } : { status: 'ready', access });
      })
      .catch((err) => {
        if (cancelled) return;
        // API anterior à rota (404 sem código): o cartão não aparece.
        if (problemStatus(err) === 404 && !problemErrorCode(err)) {
          setState({ status: 'hidden' });
          return;
        }
        // Uma releitura que falha não derruba o que já está na tela (nem o segredo recém-gerado do
        // "Conectar ao Claude", que só existe na memória dele): só avisa.
        const message = describeError(err);
        setState((current) => (current.status === 'ready' ? current : { status: 'failed', message }));
        setNotice((current) => current ?? { tone: 'error', text: `Não foi possível atualizar: ${message}` });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  async function revoke(grant: OperatorSelfGrant) {
    setRevoking(true);
    setNotice(null);
    try {
      await revokeMyOperatorGrant(grant.id);
      setConfirming(null);
      setNotice({ tone: 'info', text: 'Autorização revogada. A próxima chamada do seu Claude Code ou claude.ai recebe 401.' });
      setReloadTick((t) => t + 1);
    } catch (err) {
      setConfirming(null);
      if (problemStatus(err) === 404) {
        setNotice({ tone: 'error', text: NOT_FOUND_MESSAGE });
        setReloadTick((t) => t + 1);
      } else {
        setNotice({ tone: 'error', text: describeError(err) });
      }
    } finally {
      setRevoking(false);
    }
  }

  if (state.status === 'hidden') return null;

  return (
    <>
      {/* "Conectar ao Claude" (OA-D12) lê o mesmo acesso: a releitura depois do segredo mantém o
          estado `ready`, e o segredo recém-gerado não some com ela. */}
      {state.status === 'ready' && (
        <ConnectToClaudeCard access={state.access} onChanged={() => setReloadTick((t) => t + 1)} />
      )}
    <section className="card" aria-label={MY_OPERATOR_ACCESS_TITLE} data-testid="my-operator-access">
      <h3 style={{ fontSize: 16 }}>{MY_OPERATOR_ACCESS_TITLE}</h3>
      <p className="muted" style={{ fontSize: 14 }}>
        O seu acesso pessoal, só de leitura, ao MCP do backoffice no Claude Code ou no claude.ai. Revogar corta a
        conexão na hora; só você aprova uma conexão nova.
      </p>

      {notice && (
        <div className={notice.tone === 'error' ? 'error-banner' : 'info-banner'} role={notice.tone === 'error' ? 'alert' : 'status'}>
          {notice.text}
        </div>
      )}

      {state.status === 'loading' && <p className="muted">Carregando…</p>}
      {state.status === 'failed' && (
        <div className="error-banner" role="alert">
          Não foi possível carregar as suas autorizações: {state.message}
        </div>
      )}
      {state.status === 'ready' && (
        <AccessDetails
          access={state.access}
          confirming={confirming}
          revoking={revoking}
          onAskRevoke={(id) => {
            setNotice(null);
            setConfirming(id);
          }}
          onCancel={() => setConfirming(null)}
          onRevoke={(grant) => void revoke(grant)}
        />
      )}
    </section>
    </>
  );
}

function AccessDetails({
  access,
  confirming,
  revoking,
  onAskRevoke,
  onCancel,
  onRevoke,
}: {
  access: OperatorSelfAccess;
  confirming: string | null;
  revoking: boolean;
  onAskRevoke: (id: string) => void;
  onCancel: () => void;
  onRevoke: (grant: OperatorSelfGrant) => void;
}) {
  const { client } = access;
  const active = access.grants.filter((g) => g.status === 'Active');
  const history = access.grants.filter((g) => g.status !== 'Active');

  return (
    <>
      {!access.enabled && (
        <div className="info-banner" role="status">
          O acesso pessoal ao MCP está desligado neste ambiente: nenhuma conexão nova é aceita agora. Você ainda pode
          revogar o que estiver ativo.
        </div>
      )}

      {client ? (
        <dl className="detail-grid" style={{ margin: '0 0 12px' }}>
          <dt className="k" style={{ margin: 0 }}>Cliente OAuth</dt>
          <dd style={{ margin: 0 }} className="mono">
            {client.oauthClientId}
          </dd>
          <dt className="k" style={{ margin: 0 }}>Situação</dt>
          <dd style={{ margin: 0 }}>
            <Badge tone={client.status === 'Enabled' ? 'success' : 'neutral'} dot>
              {clientStatusLabel(client.status)}
            </Badge>
          </dd>
          <dt className="k" style={{ margin: 0 }}>Teto</dt>
          <dd style={{ margin: 0 }}>{describeScopes(client.maxScopes)}</dd>
          <dt className="k" style={{ margin: 0 }}>Recertificação</dt>
          <dd style={{ margin: 0 }}>até {formatDateTime(client.certificationDueAt)}</dd>
        </dl>
      ) : (
        <p className="muted" style={{ fontSize: 14 }} data-testid="my-operator-access-not-enabled">
          Sua conta não está habilitada para conectar o Claude neste ambiente. A habilitação é feita por um Admin, em
          Agentes de IA.
        </p>
      )}

      {client && client.status === 'Enabled' && access.enabled && active.length === 0 && (
        <p className="muted" style={{ fontSize: 13 }}>
          Nenhuma autorização ativa. {OPERATOR_INSTRUCTION}
        </p>
      )}

      {active.map((grant) => (
        <article
          key={grant.id}
          aria-label="Autorização ativa"
          data-testid={`my-operator-grant-${grant.id}`}
          style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 12 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <Badge tone="success" dot>
              {grantStatusLabel(grant.status)}
            </Badge>
            <span>{describeScopes(grant.scopes)}</span>
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Aprovada em {formatDateTime(grant.approvedAt)} · vence {formatDateTime(grant.expiresAt)} · último uso{' '}
            {formatDateTime(grant.lastUsedAt, 'nunca')}
          </div>
          {confirming === grant.id ? (
            <div role="group" aria-label="Confirmar revogação" style={{ marginTop: 10 }}>
              <p style={{ fontSize: 13, margin: '0 0 8px' }}>{REVOKE_CONFIRM_TEXT}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-danger btn-sm" disabled={revoking} onClick={() => onRevoke(grant)}>
                  {revoking ? 'Revogando…' : 'Sim, revogar'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" disabled={revoking} onClick={onCancel}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={revoking || confirming !== null}
                onClick={() => onAskRevoke(grant.id)}
              >
                Revogar
              </button>
            </div>
          )}
        </article>
      ))}

      {history.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Histórico ({history.length})</summary>
          <div className="table-scroll">
            <table aria-label="Histórico das suas autorizações">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Escopos</th>
                  <th>Aprovada</th>
                  <th>Saiu de uso</th>
                </tr>
              </thead>
              <tbody>
                {history.map((g) => (
                  <tr key={g.id}>
                    <td>{grantStatusLabel(g.status)}</td>
                    <td>{describeScopes(g.scopes)}</td>
                    <td>{formatDateTime(g.approvedAt)}</td>
                    <td>{formatDateTime(g.revokedAt ?? (g.status === 'Expired' ? g.expiresAt : null))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </>
  );
}
